import { memo, useState } from 'react';
import { useQuery } from 'react-query';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import Widget from './Widget';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import LightBox from 'components/ui/dialogs/LightBox';
import { useLocation } from 'react-router-dom';
import moment from 'moment';

function getStatusStyles(status) {
  const statuses = {
    Damda: { text: 'rgb(54, 162, 235)', bg: 'rgb(220, 245, 245)' },
    'Dam Olish Kuni': { text: 'rgb(54, 162, 235)', bg: 'rgb(220, 245, 245)' },
    'Day Off': { text: 'rgb(54, 162, 235)', bg: 'rgb(220, 245, 245)' },

    Kelgan: { text: 'rgb(75, 192, 192)', bg: 'rgb(220, 245, 220)' },
    Arrived: { text: 'rgb(75, 192, 192)', bg: 'rgb(220, 245, 220)' },

    Kechikkan: { text: 'rgb(255, 206, 86)', bg: 'rgb(255, 245, 220)' },
    'Kechikib Kelgan': { text: 'rgb(255, 206, 86)', bg: 'rgb(255, 245, 220)' },
    Late: { text: 'rgb(255, 206, 86)', bg: 'rgb(255, 245, 220)' },

    'Erta Ketgan': { text: 'rgb(153, 102, 255)', bg: 'rgb(235, 225, 255)' },
    'Early Left': { text: 'rgb(153, 102, 255)', bg: 'rgb(235, 225, 255)' },

    Kelmagan: { text: 'rgb(255, 99, 132)', bg: 'rgb(255, 220, 220)' },
    Absent: { text: 'rgb(255, 99, 132)', bg: 'rgb(255, 220, 220)' },
  };
  return statuses?.[status] ?? { text: 'black', bg: '#eee' }; // fallback
}

export default memo(function Offices() {
  const axiosPrivate = useAxiosPrivate();
  const { search } = useLocation();
  const { t } = useTranslation();
  const [openImageModal, setOpenImageModal] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [itemData, setItemData] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ['/hr/today', search],
    queryFn: async function () {
      const response = await axiosPrivate.get(`/hr/today${search}`);
      return response.data.data;
    },
    enabled: !hasError,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  const offices = data?.stats ? Object.keys(data?.stats) : [];

  return (
    <div className='h-full relative'>
      {isLoading && (
        <div className='flex items-center justify-center absolute inset-0 z-10 bg-[rgba(255,255,255,0.8)] mb-2'>
          <CircularProgress />
        </div>
      )}

      {offices?.map((office) => (
        <div className='h-[500px] mb-8'>
          <div className='h-full flex gap-3 pt-2'>
            <div className='min-w-[33%] h-full'>
              <Widget data={data} stats={data?.stats?.[office]} />
            </div>
            <div className='col-span-2 flex-grow bg-white px-5 rounded-lg h-full'>
              <div className='text-lg font-semibold mb-2 mt-4'>{office}</div>

              <TableContainer className='!border-none !shadow-none !h-[calc(100%-68px)]'>
                <Table className='!bg-white' stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell className='!bg-gray-50'></TableCell>
                      <TableCell className='!bg-gray-50'>
                        {t('common.fields.name')}
                      </TableCell>
                      <TableCell className='!bg-gray-50'>
                        {t('common.fields.status')}
                      </TableCell>
                      <TableCell className='!bg-gray-50'>
                        {t('common.table.scheduleTime')}
                      </TableCell>
                      <TableCell className='!bg-gray-50'>
                        {t('common.table.checkIn')}
                      </TableCell>
                      <TableCell className='!bg-gray-50'>
                        {t('common.table.checkOut')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className='overflow-hidden'>
                    {data?.list?.[office]?.map((row, index) => {
                      const today = moment().format('YYYY-MM-DD');
                      const time1 = moment(
                        `${today} ${row?.schedule_start}`,
                        'YYYY-MM-DD HH:mm'
                      );
                      const time2 = moment(
                        `${today} ${row?.start}`,
                        'YYYY-MM-DD HH:mm'
                      );
                      const time3 = moment(
                        `${today} ${row?.schedule_end}`,
                        'YYYY-MM-DD HH:mm'
                      );
                      const time4 = moment(
                        `${today} ${row?.end}`,
                        'YYYY-MM-DD HH:mm'
                      );

                      const isAfter = !time1.isBefore(time2);

                      const isBefore = !time3.isAfter(time4);

                      return (
                        <TableRow key={row.id} hover className='cursor-pointer'>
                          <TableCell size='small'>
                            <div className='min-h-[28px] h-full flex items-center'>
                              {row?.image ? (
                                <img
                                  src={`${row?.image}`}
                                  className='w-7 h-7 object-cover transition-all rounded-full cursor-pointer hover:brightness-50'
                                  alt=''
                                  onClick={() => {
                                    setOpenImageModal(true);
                                    setItemData({
                                      image: row?.image,
                                      name: row?.user,
                                      time: office,
                                    });
                                  }}
                                />
                              ) : (
                                <div>
                                  <i className='bi bi-no-image' />
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell size='small'>{row?.user}</TableCell>
                          <TableCell size='small'>
                            <div
                              style={{
                                background: getStatusStyles(row?.status).bg,
                                color: getStatusStyles(row?.status).text,
                              }}
                              className='inline-block p-1.5 px-3 rounded-2xl'
                            >
                              {row?.status}
                            </div>
                          </TableCell>
                          <TableCell size='small'>
                            {row?.schedule_start}-{row?.schedule_end}
                          </TableCell>
                          <TableCell size='small'>
                            <div className='w-32 flex items-center justify-between'>
                              <span
                                className='mr-2'
                                style={{
                                  color: isAfter ? 'green' : 'red',
                                }}
                              >
                                {row?.start}
                              </span>

                              {row?.start_image ? (
                                <Button
                                  size='small'
                                  color='info'
                                  variant='contained'
                                  className='!max-w-[45px]'
                                  onClick={() => {
                                    setOpenImageModal(true);
                                    setItemData({
                                      image: row?.start_image,
                                      name: row?.user,
                                      time: row?.start,
                                    });
                                  }}
                                >
                                  <i className='bi bi-person-bounding-box' />
                                </Button>
                              ) : (
                                ''
                              )}
                            </div>
                          </TableCell>
                          <TableCell size='small'>
                            <div className='w-32 flex items-center justify-between'>
                              <span
                                className='mr-2'
                                style={{
                                  color: isBefore ? 'green' : 'red',
                                }}
                              >
                                {row?.end}
                              </span>

                              {row?.end_image ? (
                                <Button
                                  size='small'
                                  color='info'
                                  variant='contained'
                                  className='!max-w-[45px]'
                                  onClick={() => {
                                    setOpenImageModal(true);
                                    setItemData({
                                      image: row?.end_image,
                                      name: row?.user,
                                      time: row?.end,
                                    });
                                  }}
                                >
                                  <i className='bi bi-person-bounding-box' />
                                </Button>
                              ) : (
                                ''
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>
        </div>
      ))}
      {openImageModal && itemData?.image ? (
        // yet-another-react-lightbox
        <LightBox
          isOpen={openImageModal}
          style={{ zIndex: 1040 }}
          captions={[`${itemData?.name} - ${itemData?.time}`]}
          titles={[`${itemData?.name} - ${itemData?.time}`]}
          images={[`${itemData?.image}`]}
          photoIndex={0}
          setIsOpen={() => {
            setOpenImageModal(false);
            setItemData({});
          }}
        />
      ) : null}
    </div>
  );
});
